import { Activity, CircleCheck, CircleX, UserRound } from "lucide-react";
import type { AdminAccount } from "../../lib/api";
import { formatDateTime, formatMoscowDateTime, formatTokens, formatUsd } from "../../lib/format";

function ConnectionState({
  connected,
  label,
}: {
  connected: boolean;
  label: string;
}) {
  const Icon = connected ? CircleCheck : CircleX;
  return (
    <div className="flex items-center gap-2">
      <Icon
        className={`h-4 w-4 ${connected ? "text-primary" : "text-muted-foreground"}`}
      />
      <span>
        {label} {connected ? "подключена" : "не подключён"}
      </span>
    </div>
  );
}

function Metric({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <div className="mt-1 font-medium">{children}</div>
    </div>
  );
}

export function OverviewTab({ account }: { account: AdminAccount }) {
  const suspended = account.status === "SUSPENDED";
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="panel p-4">
        <div className="mb-4 flex items-center gap-2">
          <UserRound className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Аккаунт</h2>
        </div>
        <div className="grid gap-4 text-sm sm:grid-cols-2">
          <Metric label="Статус">
            <span className={suspended ? "text-danger" : "text-primary"}>
              {suspended ? "Приостановлен" : "Активен"}
            </span>
          </Metric>
          <Metric label="Тариф">
            {account.tariff.name}
            <span className="block text-xs text-muted-foreground">{account.tariff.id}</span>
          </Metric>
          <Metric label="Email">
            <span>{account.user?.email ?? "не привязан"}</span>
            {account.user?.mustChangePassword ? (
              <span className="mt-1 block text-xs text-warning-foreground">
                Требуется смена пароля
              </span>
            ) : null}
          </Metric>
          <Metric label="Телефон">
            <span>{account.user?.phone ?? "не привязан"}</span>
          </Metric>
          <Metric label="Подключения">
            <span className="space-y-1 font-normal">
              <ConnectionState
                connected={account.amoConnected}
                label="amoCRM"
              />
              <ConnectionState
                connected={account.wazzupConnected}
                label="Wazzup"
              />
            </span>
          </Metric>
          {suspended && account.suspensionReason ? (
            <div className="sm:col-span-2">
              <p className="text-muted-foreground">Причина приостановки</p>
              <p className="mt-1 rounded-md border border-danger/20 bg-danger/5 p-3 text-danger">
                {account.suspensionReason}
              </p>
            </div>
          ) : null}
        </div>
      </section>

      <section className="panel p-4">
        <div className="mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Использование и наблюдаемость</h2>
        </div>
        <div className="grid gap-4 text-sm sm:grid-cols-2">
          <Metric label="Агенты">
            {formatTokens(account.funnelsCount)}
          </Metric>
          <Metric label="Квота диалогов">
            <span className="block">Использовано: {formatTokens(account.billingSummary.usedDialogs)}</span>
            <span className="block">Доступно: {formatTokens(account.billingSummary.availableDialogs)}</span>
            <span className="block">Ожидает подтверждения: {formatTokens(account.billingSummary.reservedDialogs)}</span>
          </Metric>
          <Metric label="Последний run">
            {formatDateTime(account.lastRunAt, "нет запусков")}
          </Metric>
          <Metric label="Модель LLM входящих">
            {account.llmUsage.model ?? "нет данных"}
          </Metric>
          <Metric label="Токены входящей обработки">
            {formatTokens(account.llmUsage.totalTokens)} ток.
          </Metric>
          <Metric label="Стоимость входящей обработки">
            {formatUsd(account.llmUsage.costUsd)}
          </Metric>
          <Metric label="Обновление квоты (МСК)">
            {formatMoscowDateTime(account.billingSummary.periodEndsAt)}
          </Metric>
          <Metric label="Всего диалогов">
            {formatTokens(account.dialogsCount)}
          </Metric>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          LLM-токены и стоимость — только для входящей обработки.
          Сгенерированные касания и аналитические отчёты не включены.
        </p>
      </section>
    </div>
  );
}
